import jsPDF from 'jspdf';
import { ExportConfig, getCellValue, generateFileName } from './types';

const COLORS = {
  dark: [15, 23, 42] as [number, number, number],
  gold: [196, 167, 125] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  lightGray: [245, 245, 245] as [number, number, number],
  gray: [156, 163, 175] as [number, number, number],
  text: [55, 65, 81] as [number, number, number],
};

export const exportPdfTable = (config: ExportConfig): void => {
  renderPdf(config, 'table');
};

export const exportPdfReport = (config: ExportConfig): void => {
  renderPdf(config, 'report');
};

const renderPdf = (config: ExportConfig, mode: 'table' | 'report'): void => {
  const { title, columns, data, stats, fileName } = config;
  const schrift = config.schrift ?? 6;
  const kopfSchrift = schrift === 6 ? 6 : Math.max(6, schrift - 2);
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const datum = new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
  const suffix = mode === 'report' ? 'Bericht' : 'Tabelle';

  if (mode === 'report') {
    // Dark header bar
    pdf.setFillColor(...COLORS.dark);
    pdf.rect(0, 0, pageWidth, 28, 'F');
    pdf.setTextColor(...COLORS.gold);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title.toUpperCase(), margin, 14);
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.white);
    pdf.text(`Exportiert am ${datum}`, margin, 22);
    pdf.text(`${data.length} Einträge`, pageWidth - margin - 30, 22);
    y = 35;

    // Stats box
    if (stats && stats.items.length > 0) {
      pdf.setFillColor(...COLORS.lightGray);
      pdf.roundedRect(margin, y, contentWidth, 14, 3, 3, 'F');
      const statWidth = contentWidth / stats.items.length;
      stats.items.forEach((item, i) => {
        const x = margin + statWidth * i + statWidth / 2;
        pdf.setFontSize(7);
        pdf.setTextColor(...COLORS.gray);
        pdf.setFont('helvetica', 'normal');
        pdf.text(item.label.toUpperCase(), x, y + 5, { align: 'center' });
        pdf.setFontSize(11);
        pdf.setTextColor(...COLORS.dark);
        pdf.setFont('helvetica', 'bold');
        pdf.text(String(item.value), x, y + 11, { align: 'center' });
      });
      y += 20;
    }
  } else {
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS.dark);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${title} — Export`, margin, y + 5);
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.gray);
    pdf.setFont('helvetica', 'normal');
    pdf.text(datum, pageWidth - margin - 40, y + 5);
    y += 12;
  }

  // Table
  const colWidths = calculateColWidths(columns, data, contentWidth);
  // bei 6 pt wie bisher 7/8 mm; größere Schrift → höhere Zeilen
  const rowHeight = Math.max(7, schrift * 0.75);
  const headerHeight = Math.max(8, kopfSchrift * 0.95);

  const drawHeader = () => {
    pdf.setFillColor(...(mode === 'report' ? COLORS.dark : COLORS.lightGray));
    pdf.rect(margin, y, contentWidth, headerHeight, 'F');
    pdf.setFontSize(kopfSchrift);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...(mode === 'report' ? COLORS.white : COLORS.dark));
    let x = margin + 2;
    columns.forEach((col, i) => {
      pdf.text(fitText(pdf, col.label.toUpperCase(), colWidths[i] - 3), x, y + headerHeight * 0.69);
      x += colWidths[i];
    });
    y += headerHeight;
  };

  drawHeader();

  data.forEach((row, rowIdx) => {
    // #618: bei Umbruch wird die Zeile so hoch wie die längste Zelle
    pdf.setFontSize(schrift);
    pdf.setFont('helvetica', 'normal');
    const zellenZeilen: string[][] = config.umbruch
      ? columns.map((col, i) => pdf.splitTextToSize(getCellValue(row, col), Math.max(1, colWidths[i] - 3)) as string[])
      : [];
    const meisteZeilen = Math.max(1, ...zellenZeilen.map(z => z.length));
    const zeilenHoehe = rowHeight + (meisteZeilen - 1) * schrift * 0.3528 * ZEILENFAKTOR;
    if (y + zeilenHoehe > pageHeight - 15) {
      drawFooter(pdf, pageWidth, pageHeight, margin);
      pdf.addPage();
      y = margin;
      drawHeader();
    }

    if (mode === 'report' && rowIdx % 2 === 0) {
      pdf.setFillColor(...COLORS.lightGray);
      pdf.rect(margin, y, contentWidth, zeilenHoehe, 'F');
    }

    pdf.setFontSize(schrift);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.text);
    let x = margin + 2;
    columns.forEach((col, i) => {
      const val = getCellValue(row, col);
      if (config.umbruch) pdf.text(zellenZeilen[i], x, y + rowHeight * 0.64, { lineHeightFactor: ZEILENFAKTOR });
      else pdf.text(fitText(pdf, val, colWidths[i] - 3), x, y + rowHeight * 0.64);
      x += colWidths[i];
    });
    y += zeilenHoehe;
  });

  drawFooter(pdf, pageWidth, pageHeight, margin);

  const name = (fileName || generateFileName(title, suffix)) + '.pdf';
  pdf.save(name);
};

// Kürzt Text auf die verfügbare Spaltenbreite (mm) mit '...' — verhindert das
// Überlappen in Nachbarspalten. Nutzt die aktuell gesetzte Schriftgröße/-art.
const fitText = (pdf: jsPDF, text: string, maxW: number): string => {
  if (maxW <= 0 || !text) return '';
  if (pdf.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 1 && pdf.getTextWidth(t + '...') > maxW) t = t.slice(0, -1);
  return t + '...';
};

const drawFooter = (pdf: jsPDF, pageWidth: number, pageHeight: number, margin: number): void => {
  const pages = (pdf as any).internal.getNumberOfPages();
  const current = (pdf as any).internal.getCurrentPageInfo().pageNumber;
  pdf.setFontSize(6);
  pdf.setTextColor(...COLORS.gray);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Seite ${current} von ${pages}`, pageWidth - margin - 20, pageHeight - 8);
  pdf.text(`Generiert am ${new Date().toLocaleDateString('de-DE')}`, margin, pageHeight - 8);
};

const ZEILENFAKTOR = 1.2;

const calculateColWidths = (columns: ExportConfig['columns'], data: any[], totalWidth: number): number[] => {
  if (columns.every(c => (c.breite ?? 0) > 0)) {
    const summe = columns.reduce((s, c) => s + (c.breite ?? 0), 0);
    return columns.map(c => ((c.breite ?? 0) / summe) * totalWidth);
  }
  const avgLengths = columns.map((col) => {
    const samples = data.slice(0, 50).map(row => getCellValue(row, col).length);
    const avg = samples.length > 0 ? samples.reduce((a, b) => a + b, 0) / samples.length : 5;
    return Math.max(col.label.length, avg, 5);
  });
  const total = avgLengths.reduce((a, b) => a + b, 0);
  return avgLengths.map(l => (l / total) * totalWidth);
};
